import React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, Search } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface FilterOption {
  value: string
  label: string
  /** Drawn as a dot before the label, when the thing has a colour of its own. */
  color?: string | null
  /** How many people are behind this option, when the caller knows. */
  count?: number
}

interface MultiSelectFilterProps {
  label: string
  options: FilterOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  /** Shown instead of the list when there is nothing to choose from. */
  emptyLabel?: string
  disabled?: boolean
  className?: string
}

/**
 * Pick several of something, as a filter.
 *
 * A dropdown that stays open while you tick: choosing three tags should be
 * three clicks, not three round trips through a menu that closes each time.
 *
 * The button says what is on rather than how many — "Mentor" and "Mentor +2"
 * both tell somebody what they are looking at, while "3 selected" makes them
 * open it again to find out.
 */
export const MultiSelectFilter = ({
  label,
  options,
  selected,
  onChange,
  emptyLabel,
  disabled,
  className
}: MultiSelectFilterProps) => {
  const { t } = useTranslation('common')
  const [isOpen, setIsOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isOpen) return

    const close = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }

    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [isOpen])

  const visible = React.useMemo(() => {
    const term = search.trim().toLowerCase()
    return term ? options.filter((option) => option.label.toLowerCase().includes(term)) : options
  }, [options, search])

  const toggle = (value: string) => {
    onChange(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]
    )
  }

  const chosen = options.filter((option) => selected.includes(option.value))
  const summary = chosen.length === 0
    ? label
    : chosen.length === 1
      ? chosen[0].label
      : `${chosen[0].label} +${chosen.length - 1}`

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-md border px-3 text-sm transition-colors',
          chosen.length
            ? 'border-primary bg-primary-50 text-primary-700'
            : 'border-input bg-white text-gray-700 hover:bg-gray-50',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 w-[16rem] rounded-md border bg-white shadow-lg">
          {options.length > 8 && (
            <div className="relative border-b p-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('search')}
                autoFocus
                className="h-8 w-full rounded border border-input pl-7 pr-2 text-sm outline-none focus:border-primary"
              />
            </div>
          )}

          <div className="max-h-64 overflow-y-auto p-1">
            {visible.length === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-gray-500">
                {options.length === 0 ? emptyLabel || t('empty') : t('empty')}
              </p>
            ) : (
              visible.map((option) => {
                const active = selected.includes(option.value)

                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => toggle(option.value)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors',
                      active ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    <span
                      className={cn(
                        'grid h-4 w-4 shrink-0 place-items-center rounded border',
                        active ? 'border-primary bg-primary text-white' : 'border-gray-300'
                      )}
                    >
                      {active && <Check className="h-3 w-3" />}
                    </span>

                    {option.color && (
                      <span
                        aria-hidden="true"
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: option.color }}
                      />
                    )}

                    <span className="min-w-0 flex-1 truncate">{option.label}</span>

                    {option.count !== undefined && (
                      <span className="shrink-0 text-xs text-gray-400">{option.count}</span>
                    )}
                  </button>
                )
              })
            )}
          </div>

          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full border-t px-3 py-2 text-left text-xs text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
            >
              {t('filters.clearOne', { label })}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
